import type { Company } from '../../../app/providers/CompanyProvider'
import { getTotalForCompany } from '../../balance/mocks/balanceData'

/**
 * Refunds are funded from the merchant's AVAILABLE balance, not from the
 * original payment. How far past that balance a refund may go is an
 * acquirer-level decision, not a merchant preference.
 *
 * There is only ONE variable here: how far negative the balance is allowed to
 * go. "The acquirer does not allow a negative balance" is simply a limit of
 * zero — modelling it as a separate boolean would create two ways to express
 * the same rule and two code paths that could disagree.
 */
export interface AcquirerRefundPolicy {
  /**
   * Magnitude of negative balance the acquirer permits, as a positive number.
   * 0 means the balance may never go below zero. The shortfall drawn against
   * this allowance is recovered from later payouts as a "Negative balance
   * recovery" — the same category the payout calculation already models.
   */
  negativeBalanceLimit: number
}

export type RefundFundingOutcome =
  /** No amount entered yet, and a refund is possible in principle. */
  | 'idle'
  /** Fully covered by the available balance; no negative balance needed. */
  | 'funded'
  /** Entered amount is larger than the original payment. */
  | 'exceeds_transaction'
  /** Goes negative, but stays within the acquirer's negative balance limit. */
  | 'within_negative_allowance'
  /** Beyond total funding capacity, but a smaller refund is still possible. */
  | 'over_capacity'
  /** No funding capacity at all — nothing can be refunded right now. */
  | 'no_capacity'

/**
 * Which constraint is actually binding. Drives the explanation shown: a
 * merchant who has hit their negative balance limit needs a different message
 * from one who simply has no balance.
 */
export type RefundCapReason = 'balance' | 'negative_limit'

export interface RefundFundingInput {
  /** Amount the merchant has typed, in major units. 0 when empty. */
  requestedAmount: number
  /** The original payment amount — the absolute ceiling for any refund. */
  transactionAmount: number
  /** Merchant's current available balance. May already be negative. */
  availableBalance: number
  policy: AcquirerRefundPolicy
}

export interface RefundFundingAssessment {
  outcome: RefundFundingOutcome
  /** Total that can be drawn on: available balance + negative allowance. */
  fundingCapacity: number
  /** Largest amount submittable right now, capped by the payment amount. */
  maxRefundable: number
  /** Portion funded by going negative (within the allowance). */
  drawnFromAllowance: number
  /** Portion that exceeds total funding capacity and cannot be funded. */
  unfundable: number
  /** Available balance after this refund, if submitted as requested. */
  projectedBalance: number
  /** Remaining negative headroom after this refund. */
  remainingAllowance: number
  /** Which constraint is binding when the refund is capped or refused. */
  capReason: RefundCapReason
  canSubmit: boolean
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Single source of truth for "can this refund be funded?". Both the modal's
 * warnings and its submit state read from this, so the message a merchant
 * sees can never disagree with whether the button works.
 */
export function assessRefundFunding({
  requestedAmount,
  transactionAmount,
  availableBalance,
  policy,
}: RefundFundingInput): RefundFundingAssessment {
  const limit = Math.max(policy.negativeBalanceLimit, 0)
  const spendable = Math.max(availableBalance, 0)
  // Everything the merchant can draw on: their own funds plus however far
  // the acquirer lets the balance go negative. If the balance is ALREADY
  // negative, that eats into the allowance — hence availableBalance, not
  // `spendable`, on this line.
  const fundingCapacity = round2(availableBalance + limit)
  const usableCapacity = Math.max(fundingCapacity, 0)

  const maxRefundable = round2(Math.min(transactionAmount, usableCapacity))
  const drawnFromAllowance = round2(
    Math.max(0, Math.min(requestedAmount, usableCapacity) - spendable),
  )
  const unfundable = round2(Math.max(0, requestedAmount - usableCapacity))
  const projectedBalance = round2(availableBalance - requestedAmount)
  const remainingAllowance = round2(
    Math.max(0, limit + Math.min(projectedBalance, 0)),
  )
  // When there's no allowance at all, the balance is what's binding.
  // Otherwise the merchant has run out of allowance, not just funds.
  const capReason: RefundCapReason = limit > 0 ? 'negative_limit' : 'balance'

  const base = {
    fundingCapacity,
    maxRefundable,
    drawnFromAllowance,
    unfundable,
    projectedBalance,
    remainingAllowance,
    capReason,
  }

  // Checked before the typed amount: with no capacity, the merchant should
  // learn the refund is impossible immediately rather than after typing.
  if (usableCapacity <= 0) {
    return { ...base, outcome: 'no_capacity', canSubmit: false }
  }
  if (requestedAmount <= 0) {
    return { ...base, outcome: 'idle', canSubmit: false }
  }
  if (requestedAmount > transactionAmount) {
    return { ...base, outcome: 'exceeds_transaction', canSubmit: false }
  }
  if (requestedAmount <= spendable) {
    return { ...base, outcome: 'funded', canSubmit: true }
  }
  if (requestedAmount <= usableCapacity) {
    return { ...base, outcome: 'within_negative_allowance', canSubmit: true }
  }
  return { ...base, outcome: 'over_capacity', canSubmit: false }
}

/**
 * The merchant's current available balance, read from the same deterministic
 * mock that drives Balance summary and Balance activity so the refund flow
 * can never contradict what those pages show.
 */
export function getAvailableBalance(company: Company): number {
  return getTotalForCompany(
    'available',
    company.type === 'master' ? 'master' : 'submerchant',
    company.id,
  )
}

/**
 * Acquirer policy in force for this merchant. A real implementation reads
 * this from the acquirer's merchant configuration; there is no merchant-facing
 * setting for it.
 */
export const DEFAULT_ACQUIRER_REFUND_POLICY: AcquirerRefundPolicy = {
  negativeBalanceLimit: 0,
}
